"use client";

import { langs } from "@uiw/codemirror-extensions-langs";
import { vscodeDark } from "@uiw/codemirror-theme-vscode";
import { xcodeLight } from "@uiw/codemirror-theme-xcode";
import CodeMirror, { EditorView } from "@uiw/react-codemirror";
import { Loader2 } from "lucide-react";

import { ScrollArea } from "@/components/ui/scroll-area";
import { cn } from "@/lib/utils";

import { useFileText } from "../hooks/use-file-text";
import type { FileRendererProps } from "../types";
import { UrlIframeFallback } from "./url-iframe-fallback";


interface CodeRendererProps {
  content: string;
  language?: string;
  theme?: "dark" | "light";
  className?: string;
}

// Map of language aliases to CodeMirror language support
const languageMap: Record<string, any> = {
  js: langs.javascript,
  jsx: langs.jsx,
  ts: langs.typescript,
  tsx: langs.tsx,
  html: langs.html,
  css: langs.css,
  json: langs.json,
  md: langs.markdown,
  python: langs.python,
  py: langs.python,
  rust: langs.rust,
  go: langs.go,
  java: langs.java,
  c: langs.c,
  cpp: langs.cpp,
  cs: langs.csharp,
  php: langs.php,
  ruby: langs.ruby,
  sh: langs.shell,
  bash: langs.shell,
  sql: langs.sql,
  yaml: langs.yaml,
  yml: langs.yaml,
  // Add more languages as needed
};

export function CodeRenderer({
  content,
  language = "",
  theme = "light",
  className,
}: CodeRendererProps) {


  // Determine the language extension to use
  const langExtension =
    language && languageMap[language] ? [languageMap[language]()] : [];

  // Add line wrapping extension
  const extensions = [...langExtension, EditorView.lineWrapping];



  return (
    <ScrollArea className={cn("w-full h-full", className)}>
      <div className="w-full">
        <CodeMirror
          value={content}
                  theme={theme === "dark" ? vscodeDark : xcodeLight}
          extensions={extensions}
          basicSetup={{
            lineNumbers: false,
            highlightActiveLine: false,
            highlightActiveLineGutter: false,
            foldGutter: false,
          }}
          editable={false}
          className="text-sm w-full min-h-full"
          style={{ maxWidth: "100%" }}
          height="auto"
        />
      </div>
    </ScrollArea>
  );
}

/**
 * Filename → CodeMirror language alias. Kept inside this module so the
 * registry can pick a renderer without needing to duplicate the mapping
 * table — same source of truth as the language list above.
 */
function languageFromFilename(filename: string): string {
  const ext = filename.split(".").pop()?.toLowerCase() ?? "";
  return ext in languageMap ? ext : "";
}

/**
 * Source-aware variant — used by the file-preview registry. Resolves the
 * source's text via {@link useFileText}, infers the language from the
 * filename, and delegates to the display-API `CodeRenderer`. On fetch
 * failure it falls back to {@link UrlIframeFallback} to preserve the
 * legacy `useDirectUrl` behaviour from the original attachment dialog.
 */
export function CodeSourceRenderer({ source }: FileRendererProps) {
  const state = useFileText(source);

  if (state.status === "loading" || state.status === "idle") {
    return (
      <div className="flex h-full items-center justify-center">
        <Loader2 className="size-8 animate-spin text-primary" />
      </div>
    );
  }

  if (state.status === "error") {
    return <UrlIframeFallback source={source} error={state.error} />;
  }

  return (
    <CodeRenderer
      content={state.text}
      language={languageFromFilename(source.filename)}
      className="h-full"
    />
  );
}
